import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { UserDialog } from "@/components/admin/UserDialog";

export const AdminUsers = ({ partners }) => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [editing, setEditing] = useState(undefined);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Supprimer le compte ${u.email} et ses ${u.projects_count} simulation(s) ?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Compte supprimé");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Card className="overflow-hidden" data-testid="admin-users">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="font-heading font-bold">Comptes utilisateurs</div>
        <Button onClick={() => setEditing(null)} className="bg-genis hover:bg-genis-dark text-white" data-testid="admin-add-user-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Créer un compte
        </Button>
      </div>
      {!users ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-genis" /></div>
      ) : (
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead>
            <tr className="bg-secondary/60 text-left">
              <th className="px-4 py-3 font-semibold">Nom</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Espace</th>
              <th className="px-4 py-3 font-semibold">Rôle</th>
              <th className="px-4 py-3 font-semibold text-right">Simulations</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`admin-user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">{u.partner ? <Badge variant="outline" className={u.partner.active === false ? "border-destructive text-destructive" : ""}>{u.partner.name}{u.partner.active === false ? " · désactivé" : ""}</Badge> : <span className="text-muted-foreground">Genis Energie</span>}</td>
                <td className="px-4 py-3">{u.role === "admin" ? <Badge className="bg-genis text-white">Admin</Badge> : <Badge variant="secondary">{u.partner ? "Partenaire" : "Utilisateur"}</Badge>}</td>
                <td className="px-4 py-3 text-right metric">{u.projects_count}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(u)} data-testid={`admin-edit-user-${u.email}`}><Pencil className="h-4 w-4" /></Button>
                    {u.id !== me?.id && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(u)} data-testid={`admin-delete-user-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing !== undefined && (
        <UserDialog user={editing} partners={partners} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </Card>
  );
};
